import { Link } from "react-router-dom";
import { useState } from "react";
import {
    Check,
    X,
    Sparkles,
    Palette,
    Camera,
    Users,
    Upload,
    ListChecks,
    Eye,
    Unlock,
    ArrowRight,
} from "lucide-react";
import { Reveal, LazyImage } from "../components/primitives";
import NavBar from "../components/NavBar";
import { usePricing, formatINR } from "../lib/pricing";
import { IMG, captureLead } from "../lib/api";
import { toast } from "sonner";

/* --------------------------- Hero --------------------------- */
function Hero() {
    return (
        <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28" data-testid="section-hero">
            {/* Background photograph confined to right half */}
            <div className="pointer-events-none absolute inset-0 z-0">
                <div className="absolute inset-y-0 right-0 w-full md:w-[62%]">
                    <LazyImage
                        src={IMG("hero_couple")}
                        alt="Elegant Indian couple in luxury wedding attire"
                        aspect="absolute inset-0 w-full h-full"
                        priority
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-ivory via-ivory/85 to-transparent md:from-ivory md:via-ivory/70 md:to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ivory" />
            </div>

            <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:grid-cols-12 md:px-12 lg:gap-20 lg:px-16">
                <div className="md:col-span-7 lg:col-span-6">
                    <Reveal>
                        <span className="eyebrow" data-testid="hero-eyebrow">
                            — Couple wedding visual analysis
                        </span>
                    </Reveal>
                    <Reveal delay={80}>
                        <h1
                            className="mt-6 font-serif text-[42px] leading-[1.05] tracking-tight text-charcoal sm:text-5xl md:text-[62px] md:leading-[1.04] text-balance"
                            data-testid="hero-headline"
                        >
                            Know exactly what to wear —
                            <span className="block italic text-charcoal/70">
                                before your wedding photos exist.
                            </span>
                        </h1>
                    </Reveal>
                    <Reveal delay={180}>
                        <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-ink" data-testid="hero-sub">
                            See a real sample report — then choose your event pack and get a visual harmony analysis based on your real skin tones, your venue, and how you read together on camera.
                        </p>
                    </Reveal>
                    <Reveal delay={260}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <a href="#pricing" className="btn-gold" data-testid="hero-cta-primary">
                                Choose your event pack
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </a>
                            <Link to="/sample-report" target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="hero-cta-secondary">
                                See sample report
                            </Link>
                        </div>
                        <p className="mt-5 text-xs tracking-wider text-ink/70" data-testid="hero-subtext">
                            Browse anytime <span className="mx-2 text-gold">•</span> Pay only when you choose a pack
                        </p>
                    </Reveal>
                </div>

                {/* Floating stat card */}
                <div className="md:col-span-5 lg:col-span-6 md:flex md:items-end">
                    <Reveal delay={340} className="w-full">
                        <div className="ml-auto max-w-sm rounded-3xl bg-white/70 p-6 shadow-soft backdrop-blur-2xl border border-white/60" data-testid="hero-stat-card">
                            <div className="flex items-center gap-2 text-gold">
                                <Sparkles className="h-4 w-4" />
                                <span className="text-[11px] uppercase tracking-[0.25em]">A real example</span>
                            </div>
                            <p className="mt-4 font-serif text-2xl leading-snug text-charcoal">
                                Most couples realize the coordination mistake only after the wedding photos come back.
                            </p>
                            <p className="mt-4 text-sm text-ink">
                                This is the kind of insight your personalised report reveals — once you choose a pack and upload your own photos.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* --------------------------- ChatGPT teaser --------------------------- */
function ChatGPTTeaser() {
    const chatgpt = ["You write the prompts", "You compare the answers yourself", "You coordinate bride + groom yourself"];
    const vowze = ["Guided, wedding-specific process", "Bride + groom analysed together", "One coordinated visual direction"];
    return (
        <section className="py-16 md:py-20" data-testid="section-chatgpt-teaser">
            <div className="mx-auto max-w-4xl px-6 md:px-12">
                <Reveal>
                    <div className="rounded-[28px] bg-white p-8 shadow-soft ring-1 ring-charcoal/5 md:p-10">
                        <div className="text-center">
                            <span className="eyebrow">We know what you're thinking</span>
                            <h2 className="mt-4 font-serif text-2xl leading-snug text-charcoal md:text-[32px] md:leading-[1.15] text-balance">
                                "Can't ChatGPT do this for free?"
                            </h2>
                            <p className="mx-auto mt-3 max-w-lg text-[15px] leading-relaxed text-ink">
                                Yes, it can suggest colours and generate images too. The difference is what happens after the answers.
                            </p>
                        </div>
                        <div className="mt-8 grid gap-4 sm:grid-cols-2">
                            <div className="rounded-2xl bg-champagne/40 p-5">
                                <div className="text-[11px] uppercase tracking-[0.2em] text-charcoal/50">ChatGPT</div>
                                <ul className="mt-3 space-y-2">
                                    {chatgpt.map((t) => (
                                        <li key={t} className="flex items-start gap-2.5 text-[13.5px] text-charcoal/70">
                                            <X className="mt-0.5 h-3.5 w-3.5 flex-none text-charcoal/30" />
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="rounded-2xl bg-charcoal p-5 text-ivory">
                                <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Vowze</div>
                                <ul className="mt-3 space-y-2">
                                    {vowze.map((t) => (
                                        <li key={t} className="flex items-start gap-2.5 text-[13.5px] text-ivory/90">
                                            <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-gold" />
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="mt-7 text-center">
                            <Link
                                to="/chatgpt-vs-vowze"
                                data-testid="chatgpt-teaser-link"
                                className="inline-flex items-center gap-2 text-sm font-medium text-charcoal underline decoration-gold decoration-2 underline-offset-4 transition hover:text-gold"
                            >
                                See the full comparison
                                <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Single Product --------------------------- */
function Product() {
    return (
        <section id="products" className="relative py-24 md:py-32" data-testid="section-products">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal className="mx-auto max-w-2xl text-center">
                    <span className="eyebrow">One focused product</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl">
                        Couple Wedding Visual Analysis
                    </h2>
                    <p className="mt-5 text-base text-ink">
                        See a sample, then pay from ₹399 per event — up to ₹1,499 for all 5 — for your fully personalised strategy.
                    </p>
                </Reveal>

                <div className="mx-auto mt-16 max-w-3xl">
                    <Reveal
                        className="relative flex h-full flex-col rounded-[28px] bg-white p-10 md:p-12 shadow-soft ring-1 ring-gold/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_80px_-20px_rgba(28,28,28,0.18)]"
                    >
                        <span
                            className="absolute -top-3 left-10 inline-flex items-center gap-1.5 rounded-full bg-blush px-3.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.22em] text-charcoal shadow-soft"
                            data-testid="badge-couple-highlight"
                        >
                            <Sparkles className="h-3 w-3 fill-gold stroke-gold" /> Built for weddings
                        </span>

                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                            <h3 className="font-serif text-3xl leading-tight text-charcoal md:text-4xl">
                                Wedding Look Coordination
                            </h3>
                            <span className="text-sm italic text-ink">For how you look TOGETHER</span>
                        </div>

                        <div className="mt-8 grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl bg-champagne/60 p-5">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Sample report</div>
                                <div className="mt-1.5 font-serif text-xl text-charcoal">₹0</div>
                                <ul className="mt-4 space-y-2.5">
                                    {[
                                        "A complete example report, start to finish",
                                        "10 AI-rendered portrait examples",
                                        "Full HEX colour palette breakdown",
                                        "Exactly what you'll receive, before you pay",
                                    ].map((t) => (
                                        <li key={t} className="flex items-start gap-3 text-[13px] text-charcoal/85">
                                            <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/sample-report"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="product-view-sample-link"
                                    className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-charcoal underline decoration-gold decoration-2 underline-offset-4 transition hover:text-gold"
                                >
                                    View sample report <ArrowRight className="h-3 w-3" />
                                </Link>
                            </div>
                            <div className="rounded-2xl bg-charcoal p-5 text-ivory">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Full report</div>
                                    <span className="rounded-full bg-gold/15 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-gold">1–5 events</span>
                                </div>
                                <div className="mt-1.5 flex items-baseline gap-2" data-testid="product-price">
                                    <span className="font-serif text-xl">From ₹399</span>
                                    <span className="text-[12px] text-ivory/60">up to ₹1,499 for all 5</span>
                                </div>
                                <ul className="mt-4 space-y-2.5">
                                    {[
                                        "Full outfit coordination plan",
                                        "Colour harmony palette + HEX",
                                        "Contrast optimisation between you",
                                        "Background & decor matching",
                                        "Photo outcome prediction",
                                    ].map((t) => (
                                        <li key={t} className="flex items-start gap-3 text-[13px] text-ivory/90">
                                            <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="mt-10">
                            <a
                                href="#pricing"
                                className="btn-gold w-full"
                                data-testid="card-couple-cta"
                            >
                                Choose your event pack
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </a>
                            <p className="mt-3 text-center text-xs text-ink/70">
                                View a sample first · Full report from ₹399 per event
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

/* --------------------------- Pain --------------------------- */
function Pain() {
    const bullets = [
        "Outfits that match in the store look flat in wedding photos",
        "One partner dominates the frame while the other fades",
        "Colours that fight your decor — or each other",
        "Mismatched contrast that flattens both of you on camera",
    ];
    return (
        <section className="bg-champagne/40 py-24 md:py-32" data-testid="section-pain">
            <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow">The quiet problem</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-[54px] md:leading-[1.08] text-balance">
                        Most couples realize this too late.
                    </h2>
                </Reveal>
                <Reveal delay={120}>
                    <div className="mx-auto mt-12 max-w-2xl divide-y divide-charcoal/10 text-left">
                        {bullets.map((b, i) => (
                            <div key={b} className="flex items-start gap-4 py-5">
                                <span className="mt-1 font-serif text-xs text-gold">0{i + 1}</span>
                                <p className="text-[17px] leading-relaxed text-charcoal/85">{b}</p>
                            </div>
                        ))}
                    </div>
                </Reveal>
                <Reveal delay={220}>
                    <p className="mt-12 font-serif text-2xl italic text-charcoal/75">
                        You only notice it after the wedding album arrives.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Solution --------------------------- */
function Solution() {
    return (
        <section className="relative py-28 md:py-40" data-testid="section-solution">
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 md:grid-cols-12 md:px-12">
                <Reveal className="md:col-span-5">
                    <span className="eyebrow">The shift</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-[52px] md:leading-[1.05]">
                        See how you'll read — together — before you wear it.
                    </h2>
                    <p className="mt-6 text-[17px] leading-relaxed text-ink">
                        Your actual look as a couple — based on both your real skin tones, your venue lighting, and the events across your wedding. Not a generic rule. A calibrated, coordinated opinion.
                    </p>
                    <div className="mt-8 flex items-center gap-3 text-sm text-charcoal/70">
                        <span className="hairline" />
                        Calibrated against real Indian wedding photography
                    </div>
                </Reveal>
                <Reveal delay={140} className="md:col-span-7">
                    <div className="relative">
                        <div className="absolute -inset-6 -z-10 rounded-[36px] bg-blush/40 blur-2xl" />
                        <div className="overflow-hidden rounded-[28px] shadow-soft">
                            <LazyImage
                                src={IMG("couple_coordination")}
                                alt="Couple in coordinated wedding attire"
                                aspect="aspect-[5/6]"
                            />
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Features --------------------------- */
function Features() {
    const feats = [
        {
            icon: Palette,
            img: IMG("fabric_gold"),
            title: "Skin Tone Intelligence — for both of you",
            bullets: [
                "Reads both undertones accurately",
                "Finds the palette that lifts both faces",
                "Flags colours that flatten one of you",
            ],
        },
        {
            icon: Users,
            img: IMG("couple_coordination"),
            title: "Couple Coordination Logic",
            bullets: [
                "Complement — don't clone",
                "Balance contrast between bride & groom",
                "Match the palette to your decor",
            ],
        },
        {
            icon: Camera,
            img: IMG("hands_jewelry"),
            title: "Photo Outcome Prediction",
            bullets: [
                "Models how you'll read on camera",
                "Accounts for venue lighting",
                "Prevents one partner fading out",
            ],
        },
        {
            icon: Sparkles,
            img: IMG("wedding_moment"),
            title: "Event-Specific Looks",
            bullets: [
                "Engagement, haldi, mehendi, sangeet, reception",
                "Indoor, outdoor sunlight, evening venues",
                "Calibrated to traditional / modern / indo-western",
            ],
        },
    ];
    return (
        <section className="py-24 md:py-32" data-testid="section-features">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal className="max-w-2xl">
                    <span className="eyebrow">What we actually do</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl">
                        Four layers of visual judgment — applied to both of you.
                    </h2>
                </Reveal>
                <div className="mt-16 space-y-16 md:mt-20 md:space-y-24">
                    {feats.map((f, i) => {
                        const Icon = f.icon;
                        const flip = i % 2 === 1;
                        return (
                            <Reveal key={f.title}>
                                <div className={`grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-14 ${flip ? "md:[&>div:first-child]:order-2" : ""}`}>
                                    <div className="md:col-span-6">
                                        <div className="overflow-hidden rounded-[24px] shadow-soft">
                                            <LazyImage src={f.img} alt={f.title} aspect="aspect-[5/4]" />
                                        </div>
                                    </div>
                                    <div className="md:col-span-6 md:px-4">
                                        <div className="flex items-center gap-3 text-gold">
                                            <Icon className="h-4 w-4" />
                                            <span className="text-[11px] uppercase tracking-[0.25em]">
                                                0{i + 1} — Capability
                                            </span>
                                        </div>
                                        <h3 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-[40px] md:leading-[1.1]">
                                            {f.title}
                                        </h3>
                                        <ul className="mt-6 space-y-3 text-[15px] text-ink">
                                            {f.bullets.map((b) => (
                                                <li key={b} className="flex items-start gap-3">
                                                    <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                                    <span>{b}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- Output Preview --------------------------- */
function OutputPreview() {
    return (
        <section id="preview" className="bg-champagne/40 py-24 md:py-32" data-testid="section-preview">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal className="max-w-2xl">
                    <span className="eyebrow">Sample report</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl">
                        What you receive.
                    </h2>
                    <p className="mt-5 text-ink">A real example below — then the full personalised strategy once you choose your event pack and upload your photos.</p>
                </Reveal>

                <div className="mt-14 grid gap-6 md:grid-cols-2 md:gap-8">
                    {/* EXAMPLE */}
                    <Reveal>
                        <div className="relative h-full rounded-[28px] bg-white p-8 shadow-soft md:p-10">
                            <div className="flex items-center justify-between">
                                <span className="eyebrow">Example read</span>
                                <span className="rounded-full bg-ivory px-3 py-1 text-[11px] text-charcoal/70">Illustrative example</span>
                            </div>
                            <div className="mt-7">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Visual harmony score</div>
                                <div className="mt-2 flex items-end gap-2">
                                    <span className="font-serif text-6xl leading-none text-charcoal">7.4</span>
                                    <span className="pb-2 text-sm text-ink">/ 10</span>
                                </div>
                                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-champagne">
                                    <div className="h-full rounded-full bg-gold" style={{ width: "74%" }} />
                                </div>
                            </div>
                            <dl className="mt-8 space-y-5 border-t border-charcoal/5 pt-6">
                                <div>
                                    <dt className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Combined tonal read</dt>
                                    <dd className="mt-1 text-sm text-charcoal">Warm wheatish (bride) · Medium-deep warm (groom)</dd>
                                </div>
                                <div>
                                    <dt className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Key coordination issue</dt>
                                    <dd className="mt-1 text-sm text-charcoal">Cool blue groom outfit pulls warmth out of bride's complexion under tungsten light.</dd>
                                </div>
                                <div>
                                    <dt className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60">Improvement</dt>
                                    <dd className="mt-1 text-sm text-charcoal">Anchor both outfits in warm ivory / champagne / terracotta family.</dd>
                                </div>
                            </dl>
                            <div className="mt-8 rounded-2xl bg-champagne/70 p-4 text-xs text-charcoal/70">
                                This is a real example — your own report is personalised to your photos, names and venue.
                            </div>
                        </div>
                    </Reveal>

                    {/* PAID */}
                    <Reveal delay={120}>
                        <div className="relative h-full overflow-hidden rounded-[28px] bg-charcoal p-8 text-ivory shadow-soft md:p-10">
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Full report</span>
                                <span className="rounded-full bg-gold/20 px-3 py-1 text-[11px] text-gold" data-testid="output-preview-price">
                                    From ₹399
                                </span>
                            </div>
                            <div className="mt-7">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60">Coordinated colour strategy</div>
                                <p className="mt-2 text-[15px] leading-relaxed text-ivory/90">
                                    Anchor both outfits on warm neutrals — ivory, champagne, soft camel — with the bride leaning lighter and the groom deeper to hold contrast in frame. Avoid icy blues and bright fuchsia — both collapse the couple's harmony in warm lighting.
                                </p>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {["#FAF0DE", "#D6B98C", "#B9A287", "#8C5A3A", "#6F7B55"].map((c) => (
                                        <div key={c} className="flex items-center gap-2 rounded-full bg-ivory/5 px-3 py-1.5 text-[11px]">
                                            <span className="h-3 w-3 rounded-full" style={{ background: c }} />
                                            {c}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-7 grid grid-cols-3 gap-3">
                                {[
                                    { k: "Safe", v: "Ivory lehenga + beige sherwani" },
                                    { k: "Premium", v: "Champagne silk + camel bandhgala" },
                                    { k: "Bold", v: "Terracotta lehenga + ivory bandhgala" },
                                ].map((o) => (
                                    <div key={o.k} className="rounded-2xl bg-ivory/5 p-4">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{o.k}</div>
                                        <div className="mt-1 text-[13px] leading-snug text-ivory">{o.v}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-ivory/85">
                                {[
                                    "Event-specific looks",
                                    "Contrast optimisation",
                                    "Background & decor match",
                                    "Photo outcome prediction",
                                ].map((t) => (
                                    <div key={t} className="flex items-center gap-2">
                                        <Check className="h-3.5 w-3.5 text-gold" /> {t}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={200} className="mt-10 text-center">
                    <Link
                        to="/sample-report"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="homepage-view-sample-report-btn"
                        className="btn-gold inline-flex items-center gap-2 !px-7 !py-3 text-[13px]"
                    >
                        View Full Sample Report <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- How It Works --------------------------- */
function HowItWorks() {
    const steps = [
        { icon: Eye, k: "01", title: "View a sample report", sub: "See exactly what you'll receive — a real example, no sign-up needed." },
        { icon: ListChecks, k: "02", title: "Choose your event pack", sub: "Pick how many wedding events to visualise, from ₹399 per event." },
        { icon: Upload, k: "03", title: "Upload one photo each", sub: "A clear photo of the bride and groom, plus your wedding context." },
        { icon: Unlock, k: "04", title: "Get your full strategy", sub: "Coordinated outfits, palette, and 10 AI portraits — delivered in ~90 seconds." },
    ];
    return (
        <section id="how" className="py-24 md:py-32" data-testid="section-how">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal className="max-w-2xl">
                    <span className="eyebrow">How it works</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl">
                        Four quiet steps.
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-4 md:gap-5">
                    {steps.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Reveal key={s.k} delay={i * 90}>
                                <div className="group relative h-full rounded-[24px] bg-white p-7 shadow-soft transition hover:-translate-y-1 hover:shadow-[0_24px_60px_-20px_rgba(28,28,28,0.15)]">
                                    <div className="flex items-center justify-between">
                                        <span className="font-serif text-sm text-gold">{s.k}</span>
                                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-champagne text-charcoal">
                                            <Icon className="h-4 w-4" />
                                        </span>
                                    </div>
                                    <h3 className="mt-6 font-serif text-xl leading-snug text-charcoal">{s.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-ink">{s.sub}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- Social Proof --------------------------- */
function SocialProof() {
    const quotes = [
        {
            quote: "Our wedding photos looked completely different — we finally read as one frame.",
            name: "Ishaan & Meher",
            ctx: "Couple analysis · December wedding",
            img: IMG("wedding_moment"),
        },
        {
            quote: "We almost picked colours that would've fought each other on camera. Vowze caught it before we spent a rupee on outfits.",
            name: "Arjun & Tara",
            ctx: "Couple analysis · Sangeet + reception",
            img: IMG("couple_coordination"),
        },
    ];
    return (
        <section className="bg-champagne/40 py-24 md:py-32" data-testid="section-social">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal className="max-w-2xl">
                    <span className="eyebrow">Real outcomes</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl">
                        Said better by them.
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-2 md:gap-8">
                    {quotes.map((q, i) => (
                        <Reveal key={q.name} delay={i * 120}>
                            <figure className="flex h-full flex-col overflow-hidden rounded-[28px] bg-white p-8 shadow-soft md:p-10">
                                <blockquote className="font-serif text-3xl italic leading-snug text-charcoal md:text-[34px]">
                                    "{q.quote}"
                                </blockquote>
                                <figcaption className="mt-auto flex items-center gap-4 pt-10">
                                    <div className="h-12 w-12 overflow-hidden rounded-full ring-1 ring-charcoal/10">
                                        <img src={q.img} alt={q.name} className="h-full w-full object-cover" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium text-charcoal">{q.name}</div>
                                        <div className="text-[12px] uppercase tracking-wider text-ink/70">{q.ctx}</div>
                                    </div>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- Pricing --------------------------- */
function Pricing() {
    const pricing = usePricing();
    const packs = Array.isArray(pricing?.event_packs) && pricing.event_packs.length ? pricing.event_packs : [];
    const HIGHLIGHT = { 3: "Popular", 5: "Best value" };
    return (
        <section id="pricing" className="py-24 md:py-32" data-testid="section-pricing">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal className="max-w-2xl">
                    <span className="eyebrow">Pricing, stated plainly</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-5xl">
                        See a sample. Pay only for the events you want visualised.
                    </h2>
                </Reveal>

                <div className="mx-auto mt-14 grid max-w-6xl gap-5 lg:grid-cols-[300px_1fr]">
                    {/* Sample report */}
                    <Reveal>
                        <div className="flex h-full flex-col rounded-[28px] bg-white p-8 text-charcoal shadow-soft ring-1 ring-charcoal/5">
                            <div className="text-[11px] uppercase tracking-[0.24em] opacity-70">Sample report</div>
                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-serif text-5xl leading-none">₹0</span>
                                <span className="text-[13px] opacity-70">/ View anytime</span>
                            </div>
                            <ul className="mt-8 space-y-3 text-sm">
                                {[
                                    "A complete example report, start to finish",
                                    "10 AI-rendered portrait examples",
                                    "Full HEX colour palette breakdown",
                                    "No upload or sign-up needed",
                                ].map((it) => (
                                    <li key={it} className="flex items-start gap-3">
                                        <Check className="mt-0.5 h-4 w-4 flex-none text-gold" />
                                        <span className="text-charcoal/80">{it}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/sample-report"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="pricing-cta-free"
                                className="btn-ghost mt-8 w-full"
                            >
                                View sample report
                            </Link>
                        </div>
                    </Reveal>

                    {/* Event Packs */}
                    <Reveal delay={100}>
                        <div className="flex h-full flex-col rounded-[28px] bg-charcoal p-8 text-ivory ring-1 ring-gold/50" data-testid="pricing-event-packs">
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-[11px] uppercase tracking-[0.24em] text-gold">Full report — event packs</div>
                                <span className="rounded-full bg-gold/20 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-gold">1–5 events</span>
                            </div>
                            <p className="mt-3 max-w-md text-[14px] leading-relaxed text-ivory/75">
                                Choose how many wedding events to visualise. Every pack includes bride, groom, couple and decor styling, plus your full colour palette.
                            </p>
                            <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="pricing-event-packs-grid">
                                {packs.map((p) => {
                                    const badge = HIGHLIGHT[p.events];
                                    const priceRs = Math.round(p.price_paise / 100);
                                    const individualTotalRs = Math.round(((pricing?.individual_event_paise || 39900) * p.events) / 100);
                                    const saveRs = individualTotalRs - priceRs;
                                    return (
                                        <Link
                                            key={p.events}
                                            to={`/questionnaire/couple?pack=${p.events}`}
                                            className={`group relative rounded-2xl p-4 text-center ring-1 transition hover:ring-gold/60 hover:-translate-y-0.5 ${badge ? "bg-gold/10 ring-gold/40" : "bg-ivory/5 ring-ivory/10"}`}
                                            data-testid={`pricing-pack-${p.events}`}
                                        >
                                            {badge && (
                                                <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-gold">{badge}</div>
                                            )}
                                            <div className="text-[10.5px] uppercase tracking-[0.18em] text-ivory/60">
                                                {p.events} {p.events === 1 ? "event" : "events"}
                                            </div>
                                            <div className="mt-1.5 font-serif text-2xl leading-none text-ivory">{formatINR(p.price_paise)}</div>
                                            {saveRs > 0 && (
                                                <div className="mt-1 text-[10px] font-medium text-emerald-400" data-testid={`pricing-pack-${p.events}-savings`}>
                                                    Save ₹{saveRs.toLocaleString("en-IN")}
                                                </div>
                                            )}
                                            <div className="mt-2 text-[10px] uppercase tracking-[0.14em] text-gold opacity-0 transition group-hover:opacity-100">
                                                Choose this pack
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                            <p className="mt-6 text-center text-[11px] text-ivory/50">
                                Secure payment via Razorpay · Tap a pack above to begin.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

    );
}

/* --------------------------- Trust --------------------------- */
function Trust() {
    const items = [
        "Built from real Indian wedding photography",
        "Designed from real couple coordination mistakes",
        "Focused on how the two of you read on camera",
    ];
    return (
        <section className="relative overflow-hidden py-28 md:py-36" data-testid="section-trust">
            <div className="absolute inset-0 -z-10">
                <LazyImage src={IMG("fabric_gold")} alt="Gold silk" aspect="absolute inset-0 w-full h-full" />
                <div className="absolute inset-0 bg-ivory/90" />
            </div>
            <div className="mx-auto max-w-5xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow">Built from real outcomes</span>
                    <h2 className="mt-6 font-serif text-4xl leading-tight text-charcoal md:text-[52px] md:leading-[1.06]">
                        Not trends. Not templates.
                    </h2>
                    <p className="mt-5 mx-auto max-w-2xl text-ink">
                        Vowze is calibrated against actual Indian wedding photographs and real coordination mistakes we've seen repeat across hundreds of events.
                    </p>
                </Reveal>
                <Reveal delay={120}>
                    <div className="mx-auto mt-12 grid max-w-4xl gap-4 md:grid-cols-3">
                        {items.map((it) => (
                            <div key={it} className="rounded-[22px] bg-white/80 p-6 shadow-soft backdrop-blur">
                                <p className="text-sm text-charcoal/85">{it}</p>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Final CTA --------------------------- */
function FinalCTA() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    async function submit(e) {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await captureLead(email, "final_cta");
            toast.success("You're on the list.");
            setEmail("");
        } catch {
            toast.error("Please enter a valid email.");
        } finally {
            setLoading(false);
        }
    }
    return (
        <section className="bg-charcoal py-28 text-ivory md:py-36" data-testid="section-final-cta">
            <div className="mx-auto max-w-5xl px-6 text-center md:px-12">
                <Reveal>
                    <span className="eyebrow">Start quietly</span>
                    <h2 className="mt-6 font-serif text-5xl leading-tight md:text-[72px] md:leading-[1.04] text-balance">
                        See the sample. <span className="italic text-gold/90">Then start yours.</span>
                    </h2>
                </Reveal>
                <Reveal delay={120}>
                    <div className="mx-auto mt-12 flex flex-wrap items-center justify-center gap-4">
                        <a href="#pricing" className="btn-gold" data-testid="final-cta-couple">
                            Choose your event pack
                        </a>
                        <Link
                            to="/sample-report"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="final-cta-sample"
                            className="inline-flex items-center justify-center rounded-full border border-ivory/30 bg-transparent px-8 py-4 text-sm font-medium text-ivory transition-all duration-300 hover:bg-ivory/10 hover:-translate-y-0.5"
                        >
                            View sample report
                        </Link>
                    </div>
                </Reveal>
                <Reveal delay={200}>
                    <form onSubmit={submit} className="mx-auto mt-14 flex max-w-md items-center gap-2 rounded-full border border-ivory/15 bg-ivory/5 p-1.5">
                        <input
                            data-testid="final-cta-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="flex-1 bg-transparent px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:outline-none"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="final-cta-email-submit"
                            className="rounded-full bg-gold px-5 py-3 text-sm font-medium text-charcoal transition hover:bg-goldDark disabled:opacity-60"
                        >
                            {loading ? "Saving…" : "Notify me"}
                        </button>
                    </form>
                    <p className="mt-4 text-xs text-ivory/50">We'll only write to share what's new — never sell your email.</p>
                </Reveal>
            </div>
        </section>
    );
}

/* --------------------------- Footer --------------------------- */
function Footer() {
    return (
        <footer className="border-t border-charcoal/10 bg-ivory py-10" data-testid="footer">
            <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 text-sm text-ink/70 md:flex-row md:items-center md:px-12">
                <div className="font-serif text-charcoal">Vowze<span className="text-gold">.</span></div>
                <div className="flex flex-wrap items-center gap-6">
                    <a href="/terms" className="hover:text-charcoal" data-testid="footer-terms">Terms</a>
                    <a href="/privacy" className="hover:text-charcoal" data-testid="footer-privacy">Privacy</a>
                    <span>© {new Date().getFullYear()} Vowze · Built for real weddings</span>
                </div>
            </div>
        </footer>
    );
}

/* --------------------------- Page --------------------------- */
export default function LandingPage() {
    return (
        <div className="bg-ivory">
            <NavBar />
            <Hero />
            <ChatGPTTeaser />
            <Product />
            <Pain />
            <Solution />
            <Features />
            <OutputPreview />
            <HowItWorks />
            <SocialProof />
            <Pricing />
            <Trust />
            <FinalCTA />
            <Footer />
        </div>
    );
}
